import { useEffect, useState } from "react";
import { adminApi } from "./adminApi";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Download, Trash2, CheckCircle2, Phone, Mail, Search, MessageCircle } from "lucide-react";
import { toast } from "sonner";

const STATUS_META = {
  new: { label: "New", className: "bg-primary/10 text-primary border-primary/20" },
  contacted: { label: "Contacted", className: "bg-amber-500/10 text-amber-700 border-amber-500/30" },
  closed: { label: "Closed", className: "bg-muted text-muted-foreground border-border" },
};

export default function LeadsManager() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");

  const refresh = async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/leads", { params: { limit: 500 } });
      setLeads(data);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await adminApi.get("/admin/leads", { params: { limit: 500 } });
        if (!cancelled) setLeads(data);
      } catch (e) {
        if (!cancelled) toast.error("Failed to load leads");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const setStatus = async (id, status) => {
    try {
      const { data } = await adminApi.patch(`/admin/leads/${id}`, { status });
      setLeads((arr) => arr.map((l) => (l.id === id ? data : l)));
      toast.success(`Marked as ${status}`);
    } catch (e) {
      toast.error("Failed to update");
    }
  };

  const remove = async (id) => {
    if (!confirm("Delete this lead permanently?")) return;
    try {
      await adminApi.delete(`/admin/leads/${id}`);
      setLeads((arr) => arr.filter((l) => l.id !== id));
      toast.success("Lead deleted");
    } catch (e) {
      toast.error("Failed to delete");
    }
  };

  const exportCsv = async () => {
    try {
      const res = await adminApi.get("/admin/leads/export.csv", { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `sunbright-leads-${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error("CSV export failed");
    }
  };

  const filtered = leads.filter((l) => {
    if (statusFilter !== "all" && l.status !== statusFilter) return false;
    if (!filter.trim()) return true;
    const q = filter.toLowerCase();
    return [l.full_name, l.mobile, l.message, l.course].some((v) => (v || "").toLowerCase().includes(q));
  });

  const counts = {
    all: leads.length,
    new: leads.filter((l) => l.status === "new").length,
    contacted: leads.filter((l) => l.status === "contacted").length,
    closed: leads.filter((l) => l.status === "closed").length,
  };

  return (
    <div className="space-y-5">
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            data-testid="leads-search"
            placeholder="Search name, mobile or message…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="pl-9 h-10"
          />
        </div>
        <Button onClick={exportCsv} variant="outline" data-testid="leads-export-csv">
          <Download className="h-4 w-4 mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="flex flex-wrap gap-2">
        {["all", "new", "contacted", "closed"].map((s) => (
          <button
            key={s}
            data-testid={`leads-filter-${s}`}
            onClick={() => setStatusFilter(s)}
            className={`px-3.5 py-1.5 rounded-full text-sm capitalize border transition ${
              statusFilter === s
                ? "bg-primary text-white border-primary"
                : "bg-white text-muted-foreground border-border hover:border-primary/30"
            }`}
          >
            {s} <span className="opacity-70 ml-1">{counts[s]}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-muted-foreground">Loading leads…</p>
      ) : filtered.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center">
          <p className="text-muted-foreground">No leads match your filters yet.</p>
        </div>
      ) : (
        <div className="rounded-xl border border-border bg-white overflow-hidden">
          <div className="grid grid-cols-12 px-5 py-3 bg-secondary/40 text-xs uppercase tracking-widest text-muted-foreground">
            <div className="col-span-3">Name</div>
            <div className="col-span-2">Mobile</div>
            <div className="col-span-2">Course</div>
            <div className="col-span-2">Message</div>
            <div className="col-span-1">Status</div>
            <div className="col-span-2 text-right">Actions</div>
          </div>
          {filtered.map((l) => {
            const meta = STATUS_META[l.status || "new"] || STATUS_META.new;
            return (
              <div key={l.id} data-testid={`lead-row-${l.id}`} className="grid grid-cols-12 px-5 py-4 items-start border-t border-border gap-2 text-sm">
                <div className="col-span-3">
                  <div className="font-medium">{l.full_name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{new Date(l.created_at).toLocaleString()}</div>
                </div>
                <div className="col-span-2 break-all">
                  <a href={`tel:${l.mobile}`} className="text-primary hover:underline">{l.mobile}</a>
                </div>
                <div className="col-span-2 text-muted-foreground break-words">{l.course || <span className="opacity-60">—</span>}</div>
                <div className="col-span-2 text-muted-foreground whitespace-pre-wrap break-words">{l.message}</div>
                <div className="col-span-1">
                  <Badge variant="outline" className={`${meta.className} text-xs font-medium`}>{meta.label}</Badge>
                </div>
                <div className="col-span-2 flex justify-end gap-1.5 flex-wrap">
                  <a href={`tel:${l.mobile}`} className="h-8 w-8 rounded-md flex items-center justify-center border border-border hover:border-primary hover:text-primary" title="Call"><Phone className="h-3.5 w-3.5" /></a>
                  <a href={`https://wa.me/${(l.mobile || '').replace(/\D/g, '')}`} target="_blank" rel="noopener noreferrer" className="h-8 w-8 rounded-md flex items-center justify-center border border-border hover:border-primary hover:text-primary" title="WhatsApp"><MessageCircle className="h-3.5 w-3.5" /></a>
                  <button
                    onClick={() => setStatus(l.id, l.status === "contacted" ? "new" : "contacted")}
                    data-testid={`lead-toggle-contacted-${l.id}`}
                    title="Toggle contacted"
                    className="h-8 w-8 rounded-md flex items-center justify-center border border-border hover:border-primary hover:text-primary"
                  >
                    <CheckCircle2 className="h-3.5 w-3.5" />
                  </button>
                  <button
                    onClick={() => setStatus(l.id, "closed")}
                    data-testid={`lead-close-${l.id}`}
                    title="Mark closed"
                    className="h-8 px-2 rounded-md text-xs border border-border hover:border-primary hover:text-primary"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => remove(l.id)}
                    data-testid={`lead-delete-${l.id}`}
                    title="Delete"
                    className="h-8 w-8 rounded-md flex items-center justify-center border border-border hover:border-destructive hover:text-destructive"
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
